/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { FaEnvelope, FaTag } from "react-icons/fa";
import { CampaignScreen } from "../../../../atoms/CampaingBuilderFormAtom";
import { INodeTypes } from "../../../../types/CampaignTypes";
import ActionButton from "./ActionButton";
import PlusButton from "./PlusButton";

enum ActionPostion {
  normal = "normal",
  bottom = "bottom",
  top = "top",
}

const Root = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100vw;
  height: 100vh;

  background-color: #ffffffb2;
  z-index: 9999;
`;

const RootContainer = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
`;

interface WrapperProps {
  top: number;
  left: number;
}

const Wrapper = styled.div<WrapperProps>`
  position: absolute;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
`;

const Container = styled.div`
  position: relative;
`;

interface ActProps {
  position: ActionPostion;
}

const ActionButtonWrapperContainer = styled.div<ActProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: absolute;
  bottom: ${(props) =>
    props.position === ActionPostion.normal
      ? -76
      : props.position === ActionPostion.bottom
      ? -5
      : -280}px;
  right: -80px;

  > div + div {
    margin-top: 20px;
  }
`;

type ActionButtonsType = {
  icon: JSX.Element;
  backgroundColor: string;
  tooltipLabel: string;
  screen: CampaignScreen;
  type: INodeTypes;
};

const ACTION_BUTTONS: ActionButtonsType[] = [
  {
    icon: <FaTag />,
    backgroundColor: "#97D49A",
    tooltipLabel: "Apply or remove tag(s)",
    screen: CampaignScreen.TAG,
    type: INodeTypes.ManageTag,
  },
  {
    icon: <FaEnvelope />,
    backgroundColor: "#68C2D3",
    tooltipLabel: "Send an email",
    screen: CampaignScreen.EMAIL,
    type: INodeTypes.Email,
  },
  {
    icon: (
      <img
        src={"./icons/webhook-icon.svg"}
        css={css`
          margin-top: 10px;
        `}
      />
    ),
    backgroundColor: "#997CF8",
    tooltipLabel: "Trigger a webhook",
    screen: CampaignScreen.WEBHOOK,
    type: INodeTypes.WebHook,
  },
  // {
  //   icon: <FaBell />,
  //   backgroundColor: "#E77480",
  //   tooltipLabel: "Turn on notifications",
  //   screen: CampaignScreen.PUSH_NOTIFICATION,
  //   type: INodeTypes.PushNotification,
  // },
  {
    icon: (
      <img
        src={"./icons/stop-popup.svg"}
        alt="stop"
        css={css`
          margin-top: 9px;
        `}
      />
    ),
    backgroundColor: "transparent",
    tooltipLabel: "Stop a campaign",
    screen: CampaignScreen.TAG,
    type: INodeTypes.StopTag,
  },
  // {
  //   icon: <BsFillChatFill />,
  //   backgroundColor: "#D9AA53",
  //   tooltipLabel: "Send an SMS notification",
  //   screen: CampaignScreen.SMS_NOTIFICATION,
  //   type: INodeTypes.Sms,
  // },
];

interface Props {
  isActiveButtonWrapper: number;
  handleIsActiveButtonWrapper: (vle: number) => void;
  handleActionButtonClick: (item: ActionButtonsType, index: number) => void;
  numberActived: number;
  isAtived?: boolean;
  noStopTag?: boolean;
}

export default function Actions(props: Props): JSX.Element {
  const {
    isActiveButtonWrapper,
    handleIsActiveButtonWrapper,
    handleActionButtonClick,
    numberActived,
    isAtived,
    noStopTag,
  } = props;

  const [actionPosition, setActionPosition] = useState(ActionPostion.normal);
  const containerRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  const [dropviewPosition, setDropViewPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });

  function updateDropViewPosition() {
    if (isActiveButtonWrapper === numberActived) return;
    const elementBoundingRect = (
      containerRef.current as HTMLDivElement
    ).getBoundingClientRect();

    setDropViewPosition({
      top: elementBoundingRect.top - 82,
      left: elementBoundingRect.left - 62,
    });
  }

  function closeActions() {
    handleIsActiveButtonWrapper(-1);
  }

  useEffect(() => {
    window.addEventListener("resize", closeActions);
    return () => window.removeEventListener("resize", closeActions);
  }, [containerRef, isActiveButtonWrapper, numberActived]);

  useEffect(() => {
    if (!actionsRef.current) return;
    let bottom = actionsRef.current.getBoundingClientRect().bottom;
    let top = actionsRef.current.getBoundingClientRect().top;

    if (actionPosition === ActionPostion.top && top - 135 <= 60) top -= 135;
    if (
      actionPosition === ActionPostion.bottom &&
      bottom + 135 >= window.innerHeight
    )
      bottom += 135;

    if (top <= 60) {
      setActionPosition(ActionPostion.top);
      return;
    }
    if (bottom >= window.innerHeight) {
      setActionPosition(ActionPostion.bottom);
      return;
    } else {
      setActionPosition(ActionPostion.normal);
    }
  }, [actionsRef, isActiveButtonWrapper, numberActived]);

  const Cont = (
    <Container ref={containerRef}>
      <PlusButton
        isAtived={isAtived}
        isActiveButtonWrapper={isActiveButtonWrapper === numberActived}
        onClick={(e: any) => {
          isActiveButtonWrapper === -1 && updateDropViewPosition();

          isActiveButtonWrapper === -1
            ? handleIsActiveButtonWrapper(numberActived)
            : handleIsActiveButtonWrapper(-1);

          e.stopPropagation();
        }}
      />
      {isActiveButtonWrapper === numberActived && (
        <ActionButtonWrapperContainer
          ref={actionsRef}
          position={actionPosition}
        >
          {ACTION_BUTTONS.map((item) =>
            noStopTag && item.type === INodeTypes.StopTag ? null : (
              <ActionButton
                key={item.backgroundColor}
                backgroundColor={item.backgroundColor}
                icon={item.icon}
                tooltipLabel={item.tooltipLabel}
                onClick={() => handleActionButtonClick(item, numberActived)}
              />
            )
          )}
        </ActionButtonWrapperContainer>
      )}
    </Container>
  );

  return (() => {
    return isActiveButtonWrapper === numberActived ? (
      <Root
        onClick={(e) => {
          handleIsActiveButtonWrapper(-1);
          e.stopPropagation();
        }}
      >
        <RootContainer>
          <Wrapper top={dropviewPosition.top} left={dropviewPosition.left}>
            {Cont}
          </Wrapper>
        </RootContainer>
      </Root>
    ) : (
      Cont
    );
  })();
}
